import { v4 as uuid } from 'uuid';

import {
  type ReplacementConfig,
  type Replacement,
  type ReplacementResult,
} from '../types';

export const createReplacementEngine =
  (configs: Array<ReplacementConfig<Replacement>>) =>
  (htmlContent: string): ReplacementResult => {
    // If no configs provided, return original content
    if (configs.length === 0) {
      return {
        replacements: [],
        modifiedHtml: htmlContent,
      };
    }

    // Check if any of the selectors exist in the content
    const hasAnySelector = configs.some((config) =>
      htmlContent.includes(`<${config.selector}`),
    );

    if (!hasAnySelector) {
      return {
        replacements: [],
        modifiedHtml: htmlContent,
      };
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const replacements: Array<Replacement> = [];

    configs.forEach(({ selector, processor }) => {
      const elements = doc.querySelectorAll(selector);

      elements.forEach((element) => {
        if (!(element instanceof HTMLElement)) {
          return;
        }

        const span = doc.createElement('span');
        const uniqueId = uuid();
        const spanId = `replaced-${selector}-${uniqueId}`;

        span.id = spanId;
        span.style.cssText = element.style.cssText;

        element.parentNode?.replaceChild(span, element);

        const processedData = processor(element);

        if (processedData.type) {
          const replacement: Replacement = {
            spanId,
            originalElement: element,
            ...processedData,
          } as Replacement;

          replacements.push(replacement);
        }
      });
    });

    const modifiedHtml = doc.body.innerHTML;

    return {
      replacements,
      modifiedHtml,
    };
  };
